/**
 * Types métier Phase 2 — Personnel, Dossier, Pièces, Clients, Moyens de paiement.
 *
 * Types définis manuellement en attendant `pnpm gen:api` (le schéma OpenAPI
 * Django/DRF deviendra source de vérité dès la Phase 3 quand les serializers
 * seront stabilisés). Les champs sont alignés sur les apps `personnel` et
 * `clients` décrites dans PLAN.md §2.1 / §2.2.
 */

// ── Énumérations métier ─────────────────────────────────────
export type MetierPersonnel =
  | 'NOUNOU'
  | 'AIDE_MENAGERE'
  | 'CUISINIER'
  | 'CHAUFFEUR'
  | 'COURSIER'
  | 'HOTESSE'
  | 'SECURITE';

export const METIER_LABELS: Record<MetierPersonnel, string> = {
  NOUNOU: 'Nounou',
  AIDE_MENAGERE: 'Aide ménagère',
  CUISINIER: 'Cuisinier',
  CHAUFFEUR: 'Chauffeur',
  COURSIER: 'Coursier',
  HOTESSE: 'Hôtesse',
  SECURITE: 'Agent de sécurité',
};

/**
 * Codes métier réellement acceptés par le backend (`apps.personnel.models.Metier`).
 *
 * La console utilise des libellés/clés étendus (`AIDE_MENAGERE`, `CUISINIER`)
 * côté lecture ; à la création on doit envoyer le code Django exact, sinon le
 * serializer renvoie un 400. Cette table fait la correspondance au moment du POST.
 */
export const METIER_BACKEND_CODE: Record<MetierPersonnel, string> = {
  NOUNOU: 'NOUNOU',
  AIDE_MENAGERE: 'MENAGE',
  CUISINIER: 'CUISINE',
  CHAUFFEUR: 'CHAUFFEUR',
  COURSIER: 'COURSIER',
  HOTESSE: 'HOTESSE',
  SECURITE: 'SECURITE',
};

/**
 * Statut du `Personnel` (modèle `PersonnelStatut`). À ne pas confondre avec le
 * statut du `Dossier` (cf. `StatutDossierWorkflow`), qui suit son propre cycle.
 */
export type StatutDossier =
  | 'PENDING'
  | 'VALIDATED'
  | 'PLACED'
  | 'REJECTED'
  | 'ARCHIVED';

export const STATUT_LABELS: Record<StatutDossier, string> = {
  PENDING: 'En attente',
  VALIDATED: 'Validé',
  PLACED: 'Placé',
  REJECTED: 'Rejeté',
  ARCHIVED: 'Archivé',
};

export type TypeClient = 'FOYER' | 'ENTREPRISE';

export type TypeMoyenPaiement = 'MTN_MOMO' | 'ORANGE_MONEY' | 'CARTE' | 'VIREMENT';

export const TYPE_PAIEMENT_LABELS: Record<TypeMoyenPaiement, string> = {
  MTN_MOMO: 'MTN Mobile Money',
  ORANGE_MONEY: 'Orange Money',
  CARTE: 'Carte bancaire',
  VIREMENT: 'Virement bancaire',
};

// ── Sous-objets ─────────────────────────────────────────────
export interface ContactBref {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
}

/**
 * Adresse telle que renvoyée par `AdresseSerializer` (app `clients`).
 * Champs RÉELS du modèle : `quartier`, `ville`, `repere`, `latitude`,
 * `longitude`, `plan_localisation`. Il n'y a PAS de `ligne1` ni de `pays`.
 * `latitude`/`longitude` sont des `DecimalField` sérialisés en chaîne par DRF
 * (ou `null` si non renseignés).
 */
export interface Adresse {
  id: string;
  client?: string;
  quartier: string;
  ville: string;
  repere?: string;
  latitude?: string | null;
  longitude?: string | null;
  plan_localisation?: string | null;
  created_at?: string;
}

/**
 * Statut du dossier d'onboarding (modèle `DossierStatut`). Cycle distinct du
 * statut du personnel : INCOMPLET → EN_REVUE → VALIDE / REJETE.
 */
export type StatutDossierWorkflow = 'INCOMPLET' | 'EN_REVUE' | 'VALIDE' | 'REJETE';

export const STATUT_DOSSIER_LABELS: Record<StatutDossierWorkflow, string> = {
  INCOMPLET: 'Incomplet',
  EN_REVUE: 'En revue',
  VALIDE: 'Validé',
  REJETE: 'Rejeté',
};

/**
 * Garant tel que renvoyé par `GarantSerializer` (imbriqué dans le dossier).
 * Champs réels : `nom`, `prenom`, `telephone`, `lien_parente`.
 */
export interface Garant {
  id: string;
  nom: string;
  prenom: string;
  telephone: string;
  lien_parente?: string;
  adresse?: string;
  created_at?: string;
}

/**
 * Dossier d'onboarding renvoyé par `GET /personnel/{id}/dossier/`
 * (`DossierSerializer`). Les pièces sont des `FileField` exposés en URL (ou
 * `null` si non transmises) ; `pieces_manquantes` liste les libellés requis
 * encore absents ; `is_overdue` reflète le dépassement de `deadline_at`.
 */
export interface Dossier {
  id: string;
  personnel: string;
  statut: StatutDossierWorkflow;
  cni_recto: string | null;
  cni_verso: string | null;
  cv: string | null;
  lettre_motivation: string | null;
  casier_judiciaire: string | null;
  certificat_medical: string | null;
  photo_portrait: string | null;
  photo_entiere: string | null;
  validation_par: string | null;
  validation_at: string | null;
  commentaire_validation: string;
  relance_count: number;
  deadline_at: string | null;
  garants: Garant[];
  is_overdue: boolean;
  pieces_manquantes: string[];
  /** Statuts par pièce exposés par le backend depuis Phase 2-3. */
  pieces_statut?: Partial<Record<DossierPieceKey, PieceStatutDetail>>;
  created_at: string;
  updated_at: string;
}

/** Clés de pièce acceptées par l'endpoint `POST /personnel/{id}/dossier/piece/`. */
export const DOSSIER_PIECE_KEYS = [
  'cni_recto',
  'cni_verso',
  'cv',
  'lettre_motivation',
  'casier_judiciaire',
  'certificat_medical',
  'photo_portrait',
  'photo_entiere',
  'garant',
] as const;

export type DossierPieceKey = (typeof DOSSIER_PIECE_KEYS)[number];

/** Champs de fichier du dossier (sous-ensemble des clés — sans `garant`). */
export const DOSSIER_PIECE_FIELDS = [
  'cni_recto',
  'cni_verso',
  'cv',
  'lettre_motivation',
  'casier_judiciaire',
  'certificat_medical',
  'photo_portrait',
  'photo_entiere',
] as const;

export type DossierPieceField = (typeof DOSSIER_PIECE_FIELDS)[number];

export const DOSSIER_PIECE_LABELS: Record<DossierPieceKey, string> = {
  cni_recto: 'CNI (recto)',
  cni_verso: 'CNI (verso)',
  cv: 'Curriculum Vitae',
  lettre_motivation: 'Lettre de motivation',
  casier_judiciaire: 'Casier judiciaire',
  certificat_medical: 'Certificat médical',
  photo_portrait: 'Photo portrait',
  photo_entiere: 'Photo en pied',
  garant: 'Garant',
};

/** Statut d'arbitrage d'une pièce individuelle. */
export type StatutPiece = 'VALIDE' | 'REJETE' | 'EN_ATTENTE';

export const STATUT_PIECE_LABELS: Record<StatutPiece, string> = {
  VALIDE: 'Validé',
  REJETE: 'Rejeté',
  EN_ATTENTE: 'En attente',
};

export interface PieceStatutDetail {
  statut: StatutPiece;
  commentaire: string;
}

/** Résultat de `POST /personnel/{id}/dossier/piece/`. */
export interface ArbitrerPieceInput {
  piece: DossierPieceKey;
  statut: StatutPiece;
  commentaire?: string;
}

// ── Personnel ───────────────────────────────────────────────
/**
 * Champs RÉELS renvoyés par `GET /personnel/` ET `GET /personnel/{id}/`
 * (même `PersonnelSerializer`). Liste et détail partagent donc cette forme.
 */
export interface PersonnelListItem {
  id: string;
  matricule: string;
  full_name: string;
  email?: string;
  phone?: string;
  metier: MetierPersonnel;
  statut: StatutDossier;
  certification_date?: string | null;
  secteur?: string;
  residence?: string;
  notes?: string;
  first_mandate_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Détail personnel — identique au list item (même serializer). Le dossier
 * d'onboarding se récupère séparément via `GET /personnel/{id}/dossier/`.
 */
export type Personnel = PersonnelListItem;

export interface CreatePersonnelInput {
  metier: MetierPersonnel;
  first_name: string;
  last_name: string;
  email?: string;
  telephone: string;
  secteur?: string;
  residence?: string;
  garant_nom?: string;
  garant_telephone?: string;
  garant_lien?: string;
}

/**
 * Charge utile réellement acceptée par `POST /personnel/` (PersonnelSerializer).
 * Le serializer provisionne le compte User (role=PERSONNEL) à partir de
 * `email`/`phone`/`first_name`/`last_name`. Le garant est saisi plus tard via
 * le flux dossier — on ne l'envoie donc pas à la création.
 */
export interface CreatePersonnelPayload {
  metier: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone: string;
  secteur?: string;
  residence?: string;
}

// ── Pagination ──────────────────────────────────────────────
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ── Filtres ─────────────────────────────────────────────────
export interface ListPersonnelParams {
  page?: number;
  page_size?: number;
  statut?: StatutDossier;
  metier?: MetierPersonnel;
  search?: string;
}

export interface ListClientsParams {
  page?: number;
  page_size?: number;
  type?: TypeClient;
  ville?: string;
  search?: string;
}

// ── Clients ─────────────────────────────────────────────────
/**
 * Moyen de paiement tel que renvoyé par `MoyenPaiementSerializer`.
 * Champs réels : `type`, `reference` (masquée), `est_principal`, `created_at`.
 */
export interface MoyenPaiement {
  id: string;
  client?: string;
  type: TypeMoyenPaiement;
  reference: string; // numéro téléphone ou IBAN masqué
  est_principal: boolean;
  created_at: string;
}

/**
 * Client tel que renvoyé par `ClientSerializer` — `GET /clients/` ET
 * `GET /clients/{id}/` partagent EXACTEMENT cette forme (le list item est donc
 * un alias du détail). Le compte User est mis à plat via `source="user.*"`
 * (`email`/`first_name`/`last_name`/`phone`).
 *
 * Il n'y a PAS de champs dérivés `nom_affichage`/`ville`/`telephone`/`nb_*` :
 * dérivez le nom d'affichage avec {@link nomAffichageClient}, la ville via
 * `adresses[0]?.ville`, le téléphone via `telephone_principal || phone`.
 */
export interface Client {
  id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  /** True quand l'agence a (ré)initialisé le mot de passe (à changer au login). */
  must_change_password?: boolean;
  type: TypeClient;
  raison_sociale?: string;
  numero_contribuable?: string;
  telephone_principal?: string;
  adresses: Adresse[];
  moyens_paiement: MoyenPaiement[];
  /** Nombre de retards de paiement constatés sur ce client. */
  retards_paiement: number;
  /** True si le client est marqué à risque (≥ 3 retards ou flag manuel). */
  a_risque: boolean;
  /** Date du dernier retard de paiement (format yyyy-MM-dd ou null). */
  dernier_retard_at: string | null;
  created_at: string;
  updated_at?: string;
}

/** Liste et détail partagent le même serializer : même forme. */
export type ClientListItem = Client;

/**
 * Nom d'affichage dérivé d'un client : la raison sociale pour une entreprise
 * (si renseignée), sinon « prénom nom », avec repli sur l'e-mail.
 */
export function nomAffichageClient(
  c: Pick<Client, 'type' | 'raison_sociale' | 'first_name' | 'last_name' | 'email'>,
): string {
  if (c.type === 'ENTREPRISE') {
    const raison = (c.raison_sociale ?? '').trim();
    if (raison.length > 0) return raison;
  }
  const nom = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
  if (nom.length > 0) return nom;
  return (c.email ?? '').trim();
}

export interface CreateClientInput {
  type: TypeClient;
  nom?: string;
  prenom?: string;
  raison_sociale?: string;
  numero_contribuable?: string;
  telephone: string;
  email?: string;
  ligne1: string;
  ville: string;
  quartier?: string;
  latitude?: number;
  longitude?: number;
}

/**
 * Saisie d'édition des informations d'un client (PATCH /clients/{id}/).
 * L'adresse et les moyens de paiement ont leurs propres endpoints — ils ne
 * sont pas modifiés ici.
 */
export interface UpdateClientInput {
  type: TypeClient;
  first_name?: string;
  last_name?: string;
  raison_sociale?: string;
  numero_contribuable?: string;
  telephone_principal?: string;
  email?: string;
}

/**
 * Charge utile réellement acceptée par `POST /clients/` (ClientSerializer).
 *
 * Le compte User CLIENT est provisionné à partir de `email`/`first_name`/
 * `last_name` (login par OTP). L'adresse et les moyens de paiement sont des
 * relations en lecture seule à la création : ils se renseignent via leurs
 * endpoints dédiés (`/clients/{id}/moyens-paiement/`) ou la fiche détaillée.
 */
export interface CreateClientPayload {
  type: TypeClient;
  email?: string;
  first_name?: string;
  last_name?: string;
  raison_sociale?: string;
  numero_contribuable?: string;
  telephone_principal: string;
}

export interface AddMoyenPaiementInput {
  type: TypeMoyenPaiement;
  reference: string;
  est_principal: boolean;
}

/**
 * Saisie d'adresse de la console (création client ET fiche détaillée). Le modèle
 * backend n'a pas de `ligne1` : il est fusionné dans `repere` au moment du POST
 * (cf. {@link toAddAdressePayload}). `latitude`/`longitude` sont indispensables
 * au pointage géofence (50 m) du personnel.
 */
export interface AddAdresseInput {
  ville: string;
  quartier?: string;
  ligne1?: string;
  repere?: string;
  latitude?: number;
  longitude?: number;
}
